/**
 * Házipénztár — Készpénzmozgási bizonylatok
 */
import { useState, useEffect } from 'react';
import { get, post, downloadFile } from '../../lib/api';
import { getAccessToken } from '../../lib/auth';
import { PageHeader, Button, Badge, Card, Input, Modal, EmptyState, LoadingOverlay } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import ActionsDropdown from '../../components/ActionsDropdown';
import { useFKeyBar } from '../../components/layout/FKeyBar';
import PartnerAutocomplete from '../../components/PartnerAutocomplete';
import MoneyInput from '../../components/ui/MoneyInput';

interface Receipt {
  id: string; receipt_number: string; receipt_type: 'BPB' | 'KPB'; issued_date: string;
  reason: string; partner_name?: string; partner_name_snapshot?: string;
  amount: number; rounding_diff: number; status: string; balance_after: number | null;
  invoice_number?: string; invoice_id?: string;
  storno_of_number?: string; storno_of_id?: string;
  storno_child_number?: string; storno_child_id?: string;
  storno_reason?: string; barcode_id?: string;
}

interface BalanceInfo {
  balance: number; name: string; currency: string; cashLimit: number | null; isOverLimit: boolean;
}

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
  issued: { label: 'Kiállítva', variant: 'info' },
  stornoed: { label: 'Sztornózva', variant: 'warning' },
  storno: { label: 'Sztornó', variant: 'error' },
};

function fmtDate(iso: string | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return '—'; }
}
function fmtMoney(n: number) { return Math.round(n).toLocaleString('hu-HU') + ' Ft'; }

export default function CashReceiptsPage() {
  const { toast } = useToast();
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<BalanceInfo | null>(null);
  const [showNew, setShowNew] = useState<'BPB' | 'KPB' | null>(null);

  useFKeyBar([
    { key: 'F5', label: 'Bevétel (BPB)', action: () => setShowNew('BPB') },
    { key: 'F6', label: 'Kiadás (KPB)', action: () => setShowNew('KPB') },
    { key: 'F8', label: 'Export', action: () => setShowExport(true) },
  ]);
  const [showExport, setShowExport] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);

  async function loadReceipts() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set('receiptType', typeFilter);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      const [data, bal] = await Promise.all([
        get<any>(`/cash-receipts?${params}`),
        get<BalanceInfo>('/cash-receipts/balance'),
      ]);
      setReceipts(data.data || []);
      setTotal(data.total || 0);
      setBalance(bal);
    } catch { /* */ }
    setLoading(false);
  }

  useEffect(() => { loadReceipts(); }, [typeFilter, dateFrom, dateTo, page]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      <PageHeader title="Házipénztár" description={`${total} bizonylat`}
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="ghost" onClick={() => setShowExport(true)}>Export ZIP</Button>
            <Button onClick={() => setShowNew('BPB')} style={{ background: '#16a34a', borderColor: '#16a34a' }}>+ Bevétel (BPB)</Button>
            <Button onClick={() => setShowNew('KPB')} style={{ background: '#dc2626', borderColor: '#dc2626' }}>+ Kiadás (KPB)</Button>
          </div>
        }
      />

      {/* Egyenleg kártya */}
      {balance && (
        <Card style={{ marginBottom: '14px', background: 'linear-gradient(135deg, #1e3a5f, #2563eb)', color: '#fff', padding: '16px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.7 }}>{balance.name}</div>
              <div style={{ fontSize: '28px', fontWeight: 800, marginTop: '2px' }}>{fmtMoney(balance.balance)}</div>
            </div>
            {balance.cashLimit && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '11px', opacity: 0.7 }}>Keret: {fmtMoney(balance.cashLimit)}</div>
                {balance.isOverLimit && <Badge variant="warning" style={{ fontSize: '10px' }}>Keret túllépve!</Badge>}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Szűrők */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          {[{ key: '', label: 'Mind' }, { key: 'BPB', label: 'Bevétel' }, { key: 'KPB', label: 'Kiadás' }].map(f => (
            <button key={f.key} onClick={() => { setTypeFilter(f.key); setPage(1); }}
              style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)',
                background: typeFilter === f.key ? 'var(--navy)' : 'var(--white)', color: typeFilter === f.key ? '#fff' : 'var(--ink)',
                cursor: 'pointer', fontWeight: typeFilter === f.key ? 600 : 400 }}>{f.label}</button>
          ))}
        </div>
        <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} style={{ width: '130px', fontSize: '12px' }} />
        <span style={{ color: 'var(--slate)', fontSize: '12px' }}>—</span>
        <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} style={{ width: '130px', fontSize: '12px' }} />
        <button onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
          style={{ fontSize: '11px', color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>Mind</button>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>
      ) : receipts.length === 0 ? (
        <EmptyState icon="💵" title="Még nincsenek bizonylatok" description="Készpénzmozgást a + Bevétel vagy + Kiadás gombbal rögzíthetsz." />
      ) : (
        <>
          <div style={{ display: 'grid', gap: '8px' }}>
            {receipts.map(r => {
              const st = STATUS_MAP[r.status] || STATUS_MAP.issued;
              const isBPB = r.receipt_type === 'BPB';
              const isStornoed = r.status === 'stornoed';
              const isStorno = r.status === 'storno';
              const pName = r.partner_name || r.partner_name_snapshot || '';

              return (
                <Card key={r.id} style={{
                  borderLeft: `3px solid ${isStornoed ? '#f59e0b' : isStorno ? '#ef4444' : isBPB ? '#16a34a' : '#dc2626'}`,
                  opacity: isStornoed ? 0.5 : 1,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '16px' }}>{isBPB ? '📥' : '📤'}</span>
                        <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', textTransform: 'uppercase', textDecoration: isStornoed ? 'line-through' : undefined }}>
                          {r.receipt_number}
                        </span>
                        <Badge variant={st.variant}>{st.label}</Badge>
                        <Badge variant={isBPB ? 'success' : 'error'} style={{ fontSize: '9px' }}>{isBPB ? 'Bevétel' : 'Kiadás'}</Badge>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '4px' }}>
                        {r.reason}
                        {pName && <span> · {pName}</span>}
                      </div>
                      {r.invoice_number && (
                        <div style={{ fontSize: '11px', color: '#2563eb', marginTop: '2px' }}>
                          📄 Számla: {r.invoice_number.toUpperCase()}
                        </div>
                      )}
                      {isStorno && r.storno_of_number && (
                        <div style={{ fontSize: '11px', color: '#d97706', marginTop: '2px', fontWeight: 600 }}>
                          ↩ {r.storno_of_number.toUpperCase()} sztornó bizonylata
                        </div>
                      )}
                      {isStornoed && r.storno_child_number && (
                        <div style={{ fontSize: '11px', color: 'var(--red)', marginTop: '2px' }}>
                          ❌ Sztornózva → {r.storno_child_number.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: '16px', color: isBPB ? '#16a34a' : '#dc2626' }}>
                        {isBPB ? '+' : '−'}{fmtMoney(r.amount)}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--slate)' }}>{fmtDate(r.issued_date)}</div>
                      {r.rounding_diff !== 0 && (
                        <div style={{ fontSize: '10px', color: 'var(--slate)' }}>Kerekítés: {r.rounding_diff > 0 ? '+' : ''}{fmtMoney(r.rounding_diff)}</div>
                      )}
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <ActionsDropdown actions={[
                        { label: 'PDF letöltés', icon: '📥', onClick: async () => {
                          try { await downloadFile(`/cash-receipts/${r.id}/pdf`, `${r.receipt_number}.pdf`); } catch { toast('PDF hiba.', 'error'); }
                        }},
                        ...(r.status === 'issued' ? [{
                          label: 'Sztornó', icon: '↩', onClick: async () => {
                            const reason = prompt('Sztornó oka (opcionális):');
                            if (reason === null) return;
                            try { await post(`/cash-receipts/${r.id}/storno`, { reason }); toast('Bizonylat sztornózva.', 'success'); loadReceipts(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                          }
                        }] : []),
                      ]} />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Lapozás */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: '12px' }}>← Előző</button>
              <span style={{ fontSize: '13px', color: 'var(--slate)' }}>{page} / {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: '12px' }}>Következő →</button>
            </div>
          )}
        </>
      )}

      {/* Új bizonylat modal */}
      {showNew && (
        <NewReceiptModal
          type={showNew}
          balance={balance?.balance || 0}
          onClose={() => setShowNew(null)}
          onSaved={() => { setShowNew(null); loadReceipts(); }}
        />
      )}

      {/* Export modal */}
      {showExport && (
        <ExportModal
          defaultFrom={dateFrom}
          defaultTo={dateTo}
          onClose={() => setShowExport(false)}
          onToast={toast}
        />
      )}
    </div>
  );
}

// ── Export modal ──

function ExportModal({ defaultFrom, defaultTo, onClose, onToast }: {
  defaultFrom: string; defaultTo: string; onClose: () => void;
  onToast: (msg: string, type: 'success' | 'error') => void;
}) {
  const [exportFrom, setExportFrom] = useState(defaultFrom);
  const [exportTo, setExportTo] = useState(defaultTo);
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportFrom) params.set('dateFrom', exportFrom);
      if (exportTo) params.set('dateTo', exportTo);

      const token = getAccessToken() || '';
      const resp = await fetch(`/api/export/period?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) { onToast('Export hiba.', 'error'); return; }

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const periodLabel = [exportFrom, exportTo].filter(Boolean).join('--') || 'teljes';
      a.download = `export-${periodLabel}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onToast('Export sikeresen letöltve.', 'success');
      onClose();
    } catch {
      onToast('Export hiba.', 'error');
    } finally {
      setExporting(false);
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Időszaki ZIP export">
      <div className="space-y-4">
        <p style={{ fontSize: '13px', color: 'var(--slate)' }}>
          A ZIP tartalmazza a kiállított számlák PDF-jeit, pénztárbizonylatok PDF-jeit, pénztárjelentést és CSV összesítőket.
        </p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <label className="form-label">Időszak kezdete</label>
            <Input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)} />
          </div>
          <span style={{ marginTop: '20px', color: 'var(--slate)' }}>—</span>
          <div style={{ flex: 1 }}>
            <label className="form-label">Időszak vége</label>
            <Input type="date" value={exportTo} onChange={e => setExportTo(e.target.value)} />
          </div>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--slate)', background: '#f8fafc', padding: '10px', borderRadius: 'var(--r)', border: '1px solid var(--line)' }}>
          <b>ZIP struktúra:</b><br />
          szamlak/ — számlák PDF<br />
          penztarbizonylatok/ — bizonylatok PDF<br />
          osszesito/ — penztar_jelentes.pdf, szamlak.csv, bizonylatok.csv
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        <Button variant="ghost" onClick={onClose}>Mégse</Button>
        <Button onClick={handleExport} loading={exporting}>ZIP letöltése</Button>
      </div>
    </Modal>
  );
}

// ── Új bizonylat modal ──

function NewReceiptModal({ type, balance, onClose, onSaved }: { type: 'BPB' | 'KPB'; balance: number; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [partner, setPartner] = useState<{ id: string; name: string } | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [reasons, setReasons] = useState<string[]>([]);
  const [requireAuthorizer, setRequireAuthorizer] = useState(false);
  const [requireCashier, setRequireCashier] = useState(false);
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [authorizerUserId, setAuthorizerUserId] = useState('');
  const [cashierUserId, setCashierUserId] = useState('');

  const isBPB = type === 'BPB';
  const rounded = Math.round(amount / 5) * 5;
  const roundingDiff = rounded - amount;

  useEffect(() => {
    (async () => {
      try {
        const [t, profile, teamData] = await Promise.all([
          get<any>('/cash-receipts/reason-templates'),
          get<any>('/company-profile'),
          get<any>('/team').catch(() => []),
        ]);
        setReasons(isBPB ? t.BPB : t.KPB);
        setRequireAuthorizer(profile?.cash_require_authorizer || false);
        setRequireCashier(profile?.cash_require_cashier || false);
        const teamUsers = Array.isArray(teamData) ? teamData : teamData?.data || [];
        setUsers(teamUsers.map((u: any) => ({ id: u.id, name: u.name || u.email })));
      } catch { /* */ }
    })();
  }, [type]);

  async function handleSave() {
    if (amount <= 0) { toast('Az összeg pozitív kell legyen.', 'error'); return; }
    if (!reason) { toast('A jogcím kötelező.', 'error'); return; }
    if (requireAuthorizer && !authorizerUserId) { toast('Az utalványozó kiválasztása kötelező.', 'error'); return; }
    if (requireCashier && !cashierUserId) { toast('A pénztáros kiválasztása kötelező.', 'error'); return; }
    if (type === 'KPB' && rounded > balance * 100) {
      toast(`Nincs elegendő egyenleg (${fmtMoney(balance)}).`, 'error'); return;
    }
    setSaving(true);
    try {
      const result = await post<any>('/cash-receipts', {
        receiptType: type,
        amount,
        reason,
        partnerId: partner?.id || null,
        roundToFive: true,
        notes: notes || undefined,
        authorizerUserId: authorizerUserId || undefined,
        cashierUserId: cashierUserId || undefined,
      });
      toast(`${isBPB ? 'Bevételi' : 'Kiadási'} bizonylat kiállítva: ${result.receipt_number}`, 'success');
      onSaved();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    finally { setSaving(false); }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title={isBPB ? '+ Bevételi pénztárbizonylat (BPB)' : '+ Kiadási pénztárbizonylat (KPB)'}>
      <div className="space-y-4">
        {/* Összeg */}
        <div>
          <MoneyInput label={`Összeg (Ft) *`} value={amount} onChange={setAmount} />
          {amount > 0 && roundingDiff !== 0 && (
            <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '4px' }}>
              Kerekítve: <b>{fmtMoney(rounded)}</b> (különbözet: {roundingDiff > 0 ? '+' : ''}{fmtMoney(roundingDiff)})
            </div>
          )}
          {type === 'KPB' && (
            <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '2px' }}>
              Egyenleg: {fmtMoney(balance)}
            </div>
          )}
        </div>

        {/* Jogcím gyorsgombok */}
        <div>
          <label className="form-label">Jogcím *</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
            {reasons.map(r => (
              <button key={r} onClick={() => setReason(r)}
                style={{
                  padding: '6px 12px', fontSize: '12px', borderRadius: 'var(--r)', cursor: 'pointer',
                  border: reason === r ? '2px solid var(--navy)' : '1px solid var(--line)',
                  background: reason === r ? '#eff6ff' : 'var(--white)', color: 'var(--ink)', fontWeight: reason === r ? 600 : 400,
                }}>{r}</button>
            ))}
          </div>
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Vagy írd be..." />
        </div>

        {/* Partner */}
        <PartnerAutocomplete label="Partner (opcionális)" value={partner} onChange={setPartner} placeholder="Keress partnert..." />

        {/* Utalványozó / Pénztáros */}
        {(requireAuthorizer || requireCashier) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
            {requireAuthorizer && (
              <div>
                <label className="form-label">Utalványozó *</label>
                <select value={authorizerUserId} onChange={e => setAuthorizerUserId(e.target.value)} className="form-input">
                  <option value="">Válassz...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            )}
            {requireCashier && (
              <div>
                <label className="form-label">Pénztáros *</label>
                <select value={cashierUserId} onChange={e => setCashierUserId(e.target.value)} className="form-input">
                  <option value="">Válassz...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Megjegyzés */}
        <div>
          <label className="form-label">Megjegyzés</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="form-input" placeholder="Opcionális megjegyzés..." />
        </div>

        {/* Összesítő */}
        {amount > 0 && (
          <div style={{
            padding: '12px 16px', borderRadius: 'var(--r)',
            background: isBPB ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${isBPB ? '#bbf7d0' : '#fecaca'}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, color: isBPB ? '#166534' : '#991b1b' }}>
                {isBPB ? 'Bevétel' : 'Kiadás'}
              </span>
              <span style={{ fontWeight: 800, fontSize: '18px', color: isBPB ? '#16a34a' : '#dc2626' }}>
                {isBPB ? '+' : '−'}{fmtMoney(rounded)}
              </span>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        <Button variant="ghost" onClick={onClose}>Mégse</Button>
        <Button onClick={handleSave} loading={saving}
          style={{ background: isBPB ? '#16a34a' : '#dc2626', borderColor: isBPB ? '#16a34a' : '#dc2626' }}>
          Bizonylat kiállítása
        </Button>
      </div>
    </Modal>
  );
}
